import React, { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AdminPage from "../components/admin/AdminPage";
import AdminSection from "../components/admin/AdminSection";
import { ADMIN_TOOLBAR_SX } from "../components/admin/adminStyles";
import { useOpsRun, useOpsRuns } from "../hooks/useAdminOps";
import { QUERY_PAGE_SIZE_OPTIONS } from "../utils/queryBuilder";
import { getResultsGridSx } from "../utils/resultsGrid";
import {
  OPS_JOBS,
  OPS_SPORTS,
  OPS_STATUSES,
  OPS_STATUS_CHIP,
  formatOpsDuration,
  formatOpsMetrics,
} from "../utils/opsRuns";

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString() : "—";

const AdminOps = () => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [sport, setSport] = useState("");
  const [job, setJob] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const { runs, total, isLoading, error } = useOpsRuns({
    sport,
    job,
    status,
    from,
    to,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
  });
  const {
    run: selectedRun,
    isLoading: detailLoading,
    error: detailError,
  } = useOpsRun(selectedId);

  const resetPage = (updater) => {
    updater();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = [
    { field: "sport", headerName: "Sport", width: 90 },
    { field: "job", headerName: "Job", flex: 1, minWidth: 160 },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={OPS_STATUS_CHIP[params.value] || "default"}
          size="small"
        />
      ),
    },
    {
      field: "startedAt",
      headerName: "Started",
      width: 170,
      valueFormatter: ({ value }) => formatDateTime(value),
    },
    {
      field: "finishedAt",
      headerName: "Finished",
      width: 170,
      valueFormatter: ({ value }) => formatDateTime(value),
    },
    {
      field: "durationMs",
      headerName: "Duration",
      width: 110,
      valueFormatter: ({ value }) => formatOpsDuration(value),
    },
    {
      field: "skipReason",
      headerName: "Skip reason",
      flex: 1,
      minWidth: 120,
      valueFormatter: ({ value }) => value || "—",
    },
    {
      field: "metrics",
      headerName: "Metrics",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => formatOpsMetrics(params.row.metrics),
    },
  ];

  return (
    <AdminPage
      title="Ops"
      subtitle="Did the rails run, and what happened?"
      error={error}
    >
      <AdminSection title={`${total.toLocaleString()} runs`}>
        <Box sx={{ ...ADMIN_TOOLBAR_SX, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="ops-sport-label">Sport</InputLabel>
            <Select
              labelId="ops-sport-label"
              value={sport}
              label="Sport"
              onChange={(event) =>
                resetPage(() => setSport(event.target.value))
              }
            >
              <MenuItem value="">All</MenuItem>
              {OPS_SPORTS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="ops-job-label">Job</InputLabel>
            <Select
              labelId="ops-job-label"
              value={job}
              label="Job"
              onChange={(event) => resetPage(() => setJob(event.target.value))}
            >
              <MenuItem value="">All</MenuItem>
              {OPS_JOBS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="ops-status-label">Status</InputLabel>
            <Select
              labelId="ops-status-label"
              value={status}
              label="Status"
              onChange={(event) =>
                resetPage(() => setStatus(event.target.value))
              }
            >
              <MenuItem value="">All</MenuItem>
              {OPS_STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(event) => resetPage(() => setFrom(event.target.value))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(event) => resetPage(() => setTo(event.target.value))}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box sx={{ width: "100%" }}>
          <DataGrid
            rows={runs}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            rowCount={total}
            loading={isLoading}
            disableRowSelectionOnClick
            disableColumnMenu
            onRowClick={(params) => setSelectedId(params.id)}
            rowHeight={52}
            sx={{ ...getResultsGridSx(), cursor: "pointer" }}
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  No runs yet.
                </Box>
              ),
            }}
          />
        </Box>
      </AdminSection>

      <Dialog
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Run detail</DialogTitle>
        <DialogContent>
          {detailError && (
            <Typography color="error" sx={{ mb: 1.5 }}>
              {detailError}
            </Typography>
          )}
          {detailLoading && !selectedRun ? (
            <Typography>Loading…</Typography>
          ) : selectedRun ? (
            <Box sx={{ display: "grid", gap: 1.5, pt: 1 }}>
              <Typography>
                <strong>runId:</strong> {selectedRun.runId || "—"}
              </Typography>
              <Typography>
                <strong>Duration:</strong>{" "}
                {formatOpsDuration(selectedRun.durationMs)}
              </Typography>
              <Typography>
                <strong>Metrics:</strong> {formatOpsMetrics(selectedRun.metrics)}
              </Typography>
              <Typography>
                <strong>Skip reason:</strong> {selectedRun.skipReason || "—"}
              </Typography>
              <Typography>
                <strong>Triggered by:</strong> {selectedRun.triggeredBy || "—"}
              </Typography>
              <Box>
                <Typography sx={{ mb: 0.5 }}>
                  <strong>Errors</strong>
                </Typography>
                {(selectedRun.errors || []).length === 0 ? (
                  <Typography color="text.secondary">None</Typography>
                ) : (
                  (selectedRun.errors || []).map((err, index) => (
                    <Typography key={`${err.message}-${index}`}>
                      {err.message}
                    </Typography>
                  ))
                )}
              </Box>
              <Box>
                <Typography sx={{ mb: 0.5 }}>
                  <strong>Details</strong>
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    overflow: "auto",
                    fontSize: "0.85rem",
                  }}
                >
                  {JSON.stringify(selectedRun.details ?? {}, null, 2)}
                </Box>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </AdminPage>
  );
};

export default AdminOps;
